import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inheritsLoose from "@babel/runtime/helpers/inheritsLoose";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import download from 'downloadjs';
import { Button, mineShaft, white } from '@parchment/atoms';
import { CancelIcon } from '@parchment/icons';
import { FullScreenTemplate } from '@parchment/ui-layout';
import PdfDocument from '../PdfDocument';
import PdfControls from '../PdfControls';

var DocumentPreview = /*#__PURE__*/function (_React$PureComponent) {
  _inheritsLoose(DocumentPreview, _React$PureComponent);

  function DocumentPreview(props) {
    var _this;

    _this = _React$PureComponent.call(this, props) || this;

    _defineProperty(_assertThisInitialized(_this), "componentDidUpdate", function (prevProps, nextState) {
      if (prevProps.show !== _this.props.show) {
        _this.setState({
          show: _this.props.show
        });
      }

      if (prevProps.file !== _this.props.file && nextState.scale !== 1) {
        _this.setState({
          scale: 1
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleDocumentLoaded", function (_ref) {
      var pages = _ref.numPages;

      _this.setState({
        page: 1,
        pages: pages
      });
    });

    _defineProperty(_assertThisInitialized(_this), "handlePageChange", function (page) {
      return _this.setState({
        page: page
      });
    });

    _defineProperty(_assertThisInitialized(_this), "handleZoomToFit", function (fitTo) {
      _this.setState({
        scale: 1,
        fitTo: fitTo
      });
    });

    _defineProperty(_assertThisInitialized(_this), "getClosestScale", function (farthest, currentScale) {
      // check if the number is between and min and max value
      var between = function between(num, min, max) {
        return num > min && num < max || num > max && num < min;
      }; // if the scale value is between the current scale and the closest, then use that scale, otherwise we'll use the closest still


      var reducer = function reducer(closest, scale) {
        return between(scale, currentScale, closest) ? scale : closest;
      }; // run the zoom values through the reducer using the farthest known value as the initial "closest" value


      return _this.props.zoomValues.reduce(reducer, farthest);
    });

    _defineProperty(_assertThisInitialized(_this), "handleZoomIn", function () {
      var max = Math.max.apply(Math, _this.props.zoomValues);

      var scale = _this.getClosestScale(max, _this.state.scale);

      _this.setState({
        scale: scale
      });
    });

    _defineProperty(_assertThisInitialized(_this), "handleZoomOut", function () {
      var min = Math.min.apply(Math, _this.props.zoomValues);

      var scale = _this.getClosestScale(min, _this.state.scale);

      _this.setState({
        scale: scale
      });
    });

    _defineProperty(_assertThisInitialized(_this), "close", function () {
      _this.setState({
        show: false
      });

      _this.props.onExit && _this.props.onExit();
    });

    _this.state = {
      scale: 1,
      loadedPages: {},
      fitTo: props.fitTo,
      show: props.show,
      pauseFocusTrap: false
    };
    return _this;
  }
  /**
   * Downloads the document currently in the preview
   * Note: that if the `file` prop is a url, you must omit the filename. Currently the library we use to download
   * (`downloadjs`) will only pull from a url if the filename is blank, otherwise it downloads a text file with the
   * url being the file contents.
   * @param {string} filename Optional. The filename to download as.
   * @public
   */


  var _proto = DocumentPreview.prototype;

  _proto.download = function download(filename) {
    var _this2 = this;

    //Pause focus trap
    this.setState({
      pauseFocusTrap: true
    }, function () {
      //Initiate Download
      DocumentPreview.download(_this2.props.file, filename, _this2.props.mimeType); //Unpause after timeout to remove race condition in setState

      setTimeout(function () {
        _this2.setState({
          pauseFocusTrap: false
        });
      }, 100);
    });
  }
  /**
   * Initiates a print view of the document and triggers the browser print window
   * @public
   */
  ;

  _proto.print = function print() {
    if (!this.props.printable) {
      return;
    }

    window.print();
  };

  _proto.render = function render() {
    var _this$state = this.state,
        fitTo = _this$state.fitTo,
        scale = _this$state.scale,
        pageCount = _this$state.pages,
        show = _this$state.show,
        page = _this$state.page;
    var _this$props = this.props,
        title = _this$props.title,
        toolbar = _this$props.toolbar,
        printable = _this$props.printable,
        blockRightClick = _this$props.blockRightClick,
        zoomValues = _this$props.zoomValues,
        exitOnEscape = _this$props.exitOnEscape,
        inline = _this$props.inline,
        file = _this$props.file,
        footer = _this$props.footer,
        zIndex = _this$props.zIndex;
    var preview = /*#__PURE__*/React.createElement(PreviewContainer, {
      printable: printable
    }, /*#__PURE__*/React.createElement(Header, null, /*#__PURE__*/React.createElement(PdfControls, {
      fitTo: fitTo,
      onZoomToFit: this.handleZoomToFit,
      canZoomIn: Math.max.apply(Math, zoomValues) > scale,
      canZoomOut: Math.min.apply(Math, zoomValues) < scale,
      onZoomIn: this.handleZoomIn,
      onZoomOut: this.handleZoomOut
    }), /*#__PURE__*/React.createElement(Title, {
      className: "title"
    }, title), /*#__PURE__*/React.createElement(Toolbar, null, toolbar, !inline && /*#__PURE__*/React.createElement(CloseButton, {
      type: "button",
      onClick: this.close,
      "aria-label": "Close Preview",
      name: "close"
    }, /*#__PURE__*/React.createElement(CancelIcon, null)))), /*#__PURE__*/React.createElement(Body, null, /*#__PURE__*/React.createElement(PdfDocument, {
      file: file,
      scale: scale,
      fitTo: fitTo,
      onLoad: this.handleDocumentLoaded,
      onPageChange: this.handlePageChange,
      blockRightClick: blockRightClick
    })), /*#__PURE__*/React.createElement(Footer, null, pageCount && /*#__PURE__*/React.createElement("div", {
      className: "page-counter"
    }, "Page ", page, " of ", pageCount), footer));

    if (inline) {
      return preview;
    }

    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Overlay, {
      className: "document-preview-modal fit-to-" + fitTo + " " + (printable ? '' : 'not-') + "printable",
      backdropClassName: 'document-preview-backdrop',
      open: show,
      onClose: this.close,
      closeOnEsc: exitOnEscape,
      printable: printable,
      zIndex: zIndex,
      pauseFocusTrap: this.state.pauseFocusTrap
    }, preview));
  };

  return DocumentPreview;
}(React.PureComponent);

_defineProperty(DocumentPreview, "download", download);

_defineProperty(DocumentPreview, "propTypes", {
  /**
   * Whether the modal should be shown or not
   */
  show: PropTypes.bool,

  /**
   * Method to invoke when the modal is closed
   */
  onExit: PropTypes.func,

  /**
   * Initial zoom setting to display the page in. `page` Displays the entire page in the view port and `width` zooms in so the page takes width of the view port
   */
  fitTo: PropTypes.oneOf(['page', 'width']),

  /**
   * Component to add to the toolbar
   */
  toolbar: PropTypes.node,

  /**
   * The url to the file. Can be an http(s) url or a data uri (i.e. "data:application/pdf;base64,<base64_encoded_file>")
   */
  file: PropTypes.string,

  /**
   * The text/component to put in the modal title
   */
  title: PropTypes.node,

  /**
   * Whether the modal should exit when the escape key is pressed
   */
  exitOnEscape: PropTypes.bool,

  /**
   * The document mime type (used when downloading). Optional.
   */
  mimeType: PropTypes.string,

  /**
   * Component to put in the footer of the modal.
   */
  footer: PropTypes.node,

  /**
   * Whether the document should be printable or not. When set to false, the modal will not be visible when the
   * user attempts to print the page. Note: That currently there is no way to nicely print the document from this
   * DocumentPreview component. If the user were to attempt to print the page (and assuming `printable={true}`),
   * then they would just get the pages at whatever the current zoom level is overlaid on the actual webpage they
   * are on.
   */
  printable: PropTypes.bool,

  /**
   * Whether or not right click is allowed
   */
  blockRightClick: PropTypes.bool,

  /**
   * The zoom levels that pressing the zoom in/out will jump to
   */
  zoomValues: PropTypes.arrayOf(PropTypes.number),

  /**
   * Decides if the preview is displayed as a modal or not
   */
  inline: PropTypes.bool,

  /**
   * z-index to overwrite the default (9)
   */
  zIndex: PropTypes.number
});

_defineProperty(DocumentPreview, "defaultProps", {
  fitTo: 'page',
  show: false,
  title: 'Document Preview',
  exitOnEscape: true,
  printable: true,
  blockRightClick: false,
  zoomValues: [0.5, 0.75, 0.9, 1, 1.25, 1.5, 2],
  inline: false,
  zIndex: 9
});

export { DocumentPreview as default };
var CloseButton = /*#__PURE__*/styled(Button).withConfig({
  displayName: "DocumentPreview__CloseButton",
  componentId: "qqkwxc-0"
})(["margin:0 5px;"]);
CloseButton.displayName = 'CloseButton';
var Header = /*#__PURE__*/styled.div.withConfig({
  displayName: "DocumentPreview__Header",
  componentId: "qqkwxc-1"
})(["display:flex;justify-content:space-between;align-items:center;padding:15px;background:", ";height:10%;"], mineShaft);
var Title = /*#__PURE__*/styled.h4.withConfig({
  displayName: "DocumentPreview__Title",
  componentId: "qqkwxc-2"
})(["flex:1;font-size:20px;width:auto;color:", ";text-align:center;@media (min-width:960px){order:initial}"], white);
Title.displayName = 'Title';
var Toolbar = /*#__PURE__*/styled.div.withConfig({
  displayName: "DocumentPreview__Toolbar",
  componentId: "qqkwxc-3"
})(["flex:1;display:flex;flex-direction:row;flex-wrap:nowrap;white-space:nowrap;order:2;justify-content:flex-end;@media (min-width:960px){order:initial;}"]);
Toolbar.displayName = 'Toolbar';
var Body = /*#__PURE__*/styled.div.withConfig({
  displayName: "DocumentPreview__Body",
  componentId: "qqkwxc-4"
})(["position:relative;padding:0;text-align:center;height:84%;"]);
var Footer = /*#__PURE__*/styled.div.withConfig({
  displayName: "DocumentPreview__Footer",
  componentId: "qqkwxc-5"
})(["display:flex;justify-content:center;align-items:center;padding:10px;background:", ";color:", ";height:6%;"], mineShaft, white);
var hideForPrintCss = /*#__PURE__*/css(["@media print{display:none;}"]);

var hideForPrint = function hideForPrint(_ref2) {
  var _ref2$printable = _ref2.printable,
      printable = _ref2$printable === void 0 ? true : _ref2$printable;
  return !printable && hideForPrintCss;
};

var Overlay = /*#__PURE__*/styled(FullScreenTemplate).withConfig({
  displayName: "DocumentPreview__Overlay",
  componentId: "qqkwxc-6"
})(["background-color:rgba(0,0,0,0.5);", ""], hideForPrint);
var PreviewContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "DocumentPreview__PreviewContainer",
  componentId: "qqkwxc-7"
})(["height:100%;background:rgba(0,0,0,0.7);", ""], hideForPrint);